import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { searchImages } from '../api/apiClient';
import MasonryGrid from '../components/MasonryGrid';
import Seo from '../components/Seo';
import { Loader2 } from 'lucide-react';

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q');
  const [results, setResults] = useState([]);
  const [tags, setTags] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const BATCH_SIZE = 20;

  useEffect(() => {
    const fetchResults = async () => {
      if (query) {
        try {
          setLoading(true);
          const data = await searchImages(query, { limit: BATCH_SIZE, skip: 0 });
          setResults(data.images);
          setTags(data.tags || []);
          setPhotographers(data.photographers || []);
          setTotal(data.total);
          setHasMore(data.images.length >= BATCH_SIZE);
        } catch (err) {
          console.error('Error searching images:', err);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchResults();
  }, [query]);

  const loadMore = async () => {
    try {
      setLoadingMore(true);
      const data = await searchImages(query, { limit: BATCH_SIZE, skip: results.length });
      setResults(prev => [...prev, ...data.images]);
      setHasMore(data.images.length >= BATCH_SIZE);
    } catch (err) {
      console.error('Error loading more results:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Seo title={query ? `Search: ${query}` : 'Search'} noindex />
      {/* Header */}
      <div className="bg-gray-50 border-b border-gray-200 py-8 px-4">
        <div className="max-w-full md:max-w-[70%] mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Search Results for "{query}"
          </h1>
          {tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="search-tags">
              <span className="text-gray-600 font-medium">Tags:</span>
              {tags.map((t) => (
                <Link
                  key={t.cat_id}
                  to={t.slug}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded transition-colors inline-block"
                  data-testid={`search-tag-${t.cat_id}`}
                >
                  {t.label}
                </Link>
              ))}
            </div>
          )}
          {photographers.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="search-photographers">
              <span className="text-gray-600 font-medium">Photographers:</span>
              {photographers.map((p) => (
                <Link
                  key={p.user_id}
                  to={p.user_url}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded transition-colors inline-block"
                  data-testid={`search-photographer-${p.user_id}`}
                >
                  {p.user_display_name}
                </Link>
              ))}
            </div>
          )}
          <p className="text-gray-600">
            {total} {total === 1 ? 'photo' : 'photos'} found
          </p>
        </div>
      </div>

      {/* Results Grid */}
      <div className="py-8">
        <MasonryGrid
          images={results}
          showPhotographer={true}
          showTags={true}
          photographerClickable={true}
          tagsClickable={true}
          hasMore={hasMore}
          onLoadMore={loadMore}
          loadingMore={loadingMore}
        />
      </div>
    </div>
  );
};

export default SearchResultsPage;